<template>
  <div ref="containerRef" class="relative w-full min-h-full">
    <!-- Floating Pull-to-Refresh Pill Indicator -->
    <div
      class="fixed left-1/2 -translate-x-1/2 z-[60] pointer-events-none select-none transition-all duration-200"
      :style="indicatorStyle"
    >
      <div
        class="flex items-center gap-2.5 px-4 py-2 rounded-full shadow-lg border backdrop-blur-md transition-colors duration-200 text-xs font-semibold"
        :class="[
          isThresholdPassed || props.refreshing
            ? 'bg-white/95 dark:bg-slate-800/95 border-indigo-400/80 dark:border-indigo-500/80 text-indigo-600 dark:text-indigo-400 ring-2 ring-indigo-500/20'
            : 'bg-white/90 dark:bg-slate-800/90 border-slate-200/80 dark:border-slate-700/80 text-slate-600 dark:text-slate-300'
        ]"
      >
        <ArrowPathIcon
          class="w-4 h-4 shrink-0 transition-transform duration-75"
          :class="{ 'animate-spin': props.refreshing }"
          :style="!props.refreshing ? { transform: `rotate(${iconRotation}deg)` } : {}"
        />
        <span>{{ statusText }}</span>
      </div>
    </div>

    <!-- Slotted Content -->
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  refreshing: {
    type: Boolean,
    default: false
  },
  threshold: {
    type: Number,
    default: 65
  },
  maxPull: {
    type: Number,
    default: 110
  },
  damping: {
    type: Number,
    default: 0.45
  },
  disabled: {
    type: Boolean,
    default: false
  },
  topOffset: {
    type: String,
    default: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)'
  }
});

const emit = defineEmits(['refresh']);

const { t } = useI18n();

const containerRef = ref(null);
const pullDistance = ref(0);
const isDragging = ref(false);

let startY = 0;
let startX = 0;
let canPull = false;

const isThresholdPassed = computed(() => pullDistance.value >= props.threshold);

const iconRotation = computed(() => {
  const progress = Math.min(1, pullDistance.value / props.threshold);
  return Math.round(progress * 360);
});

const statusText = computed(() => {
  if (props.refreshing) {
    return t('profileSection.refreshing') || 'Atualizando dados...';
  }
  if (isThresholdPassed.value) {
    return t('profileSection.releaseToRefresh') || 'Solte para atualizar';
  }
  return t('profileSection.pullToRefresh') || 'Puxe para atualizar';
});

const indicatorStyle = computed(() => {
  const topPos = props.topOffset;

  if (props.refreshing) {
    return {
      top: topPos,
      opacity: 1,
      transform: 'translate(-50%, 0) scale(1)',
      transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
    };
  }

  if (pullDistance.value <= 0) {
    return {
      top: topPos,
      opacity: 0,
      transform: 'translate(-50%, -20px) scale(0.85)',
      transition: isDragging.value ? 'none' : 'all 0.25s ease-out'
    };
  }

  const progress = Math.min(1, pullDistance.value / props.threshold);
  const translateY = Math.min(32, pullDistance.value * 0.45);
  const opacity = Math.min(1, progress * 1.35);
  const scale = 0.85 + (progress * 0.15);

  return {
    top: topPos,
    opacity,
    transform: `translate(-50%, ${translateY}px) scale(${scale})`,
    transition: isDragging.value ? 'none' : 'all 0.25s ease-out'
  };
});

/**
 * Checks whether all scroll containers (and window) are at the top (scrollTop <= 1px).
 * Subpixel precision (<= 1) avoids jitter on retina displays.
 */
const isScrolledToTop = () => {
  if (typeof window === 'undefined') return true;
  if (window.scrollY > 1 || (document.documentElement && document.documentElement.scrollTop > 1) || (document.body && document.body.scrollTop > 1)) {
    return false;
  }
  let el = containerRef.value;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.scrollTop > 1) {
      return false;
    }
    el = el.parentElement;
  }
  return true;
};

const handleTouchStart = (e) => {
  if (props.disabled || props.refreshing) return;
  if (!e.touches || e.touches.length === 0) return;

  if (isScrolledToTop()) {
    canPull = true;
    startY = e.touches[0].clientY;
    startX = e.touches[0].clientX;
  } else {
    canPull = false;
  }
};

const handleTouchMove = (e) => {
  if (!canPull || props.disabled || props.refreshing) return;
  if (!e.touches || e.touches.length === 0) return;

  const currentY = e.touches[0].clientY;
  const currentX = e.touches[0].clientX;
  const deltaX = Math.abs(currentX - startX);
  const deltaY = currentY - startY;

  // Ignore horizontal swipes (side menu drawer, tabs)
  if (deltaX > Math.abs(deltaY) && deltaX > 10) {
    canPull = false;
    pullDistance.value = 0;
    isDragging.value = false;
    return;
  }

  // Pulling downward from top
  if (deltaY > 6 && isScrolledToTop()) {
    isDragging.value = true;
    if (e.cancelable) {
      e.preventDefault();
    }
    const damped = (deltaY - 6) * props.damping;
    pullDistance.value = Math.min(props.maxPull, Math.max(0, damped));
  } else if (deltaY <= 0) {
    pullDistance.value = 0;
    isDragging.value = false;
  }
};

const handleTouchEnd = () => {
  if (!canPull) return;
  canPull = false;
  isDragging.value = false;

  if (pullDistance.value >= props.threshold && !props.refreshing) {
    pullDistance.value = props.threshold;
    emit('refresh');
  } else {
    pullDistance.value = 0;
  }
};

watch(() => props.refreshing, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    pullDistance.value = 0;
    isDragging.value = false;
  }
});

onMounted(() => {
  const el = containerRef.value;
  if (!el) return;

  // Non-passive listener allows calling preventDefault() smoothly during downward pull
  el.addEventListener('touchstart', handleTouchStart, { passive: true });
  el.addEventListener('touchmove', handleTouchMove, { passive: false });
  el.addEventListener('touchend', handleTouchEnd, { passive: true });
  el.addEventListener('touchcancel', handleTouchEnd, { passive: true });
});

onBeforeUnmount(() => {
  const el = containerRef.value;
  if (!el) return;

  el.removeEventListener('touchstart', handleTouchStart);
  el.removeEventListener('touchmove', handleTouchMove);
  el.removeEventListener('touchend', handleTouchEnd);
  el.removeEventListener('touchcancel', handleTouchEnd);
});
</script>
